
import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Progress } from '@/components/ui/progress';

// Add YouTube types
declare global {
  interface Window {
    onYouTubeIframeAPIReady?: () => void;
    YT: {
      Player: new (iframe: HTMLIFrameElement, options: any) => any;
      PlayerState: {
        PLAYING: number;
      };
    };
  }
}

interface ModuleContent {
  id: string;
  title: string;
  description: string | null;
  content_type: string;
  url: string;
  order_number: number;
}

interface VideoProgress {
  progress_percentage: number;
  last_position: number;
}

const ModuleContent = () => {
  const { moduleId } = useParams();
  const [content, setContent] = useState<ModuleContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleTitle, setModuleTitle] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [videoProgress, setVideoProgress] = useState<Record<string, VideoProgress>>({});
  const progressUpdateTimeout = useRef<Record<string, NodeJS.Timeout>>({});
  const { toast } = useToast();
  const [youtubeApiReady, setYoutubeApiReady] = useState(false);

  useEffect(() => {
    // Load YouTube API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      setYoutubeApiReady(true);
    };

    return () => {
      window.onYouTubeIframeAPIReady = undefined;
    };
  }, []);

  useEffect(() => {
    fetchModuleContent();
    fetchVideoProgress();
  }, [moduleId]);

  const fetchVideoProgress = async () => {
    const { data: progressData, error } = await supabase
      .from('user_video_progress')
      .select('content_id, progress_percentage, last_position');

    if (error) {
      console.error('Error fetching video progress:', error);
      return;
    }

    const progressMap: Record<string, VideoProgress> = {};
    progressData?.forEach(item => {
      progressMap[item.content_id] = {
        progress_percentage: item.progress_percentage,
        last_position: item.last_position
      };
    });
    setVideoProgress(progressMap);
  };

  const updateVideoProgress = async (contentId: string, currentTime: number, duration: number) => {
    // Clear existing timeout for this video
    if (progressUpdateTimeout.current[contentId]) {
      clearTimeout(progressUpdateTimeout.current[contentId]);
    }

    // Set a new timeout to update progress after 2 seconds of no updates
    progressUpdateTimeout.current[contentId] = setTimeout(async () => {
      const progress_percentage = Math.round((currentTime / duration) * 100);
      
      try {
        const { error } = await supabase
          .from('user_video_progress')
          .upsert({
            content_id: contentId,
            user_id: (await supabase.auth.getUser()).data.user?.id,
            progress_percentage,
            last_position: currentTime,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,content_id'
          });

        if (error) throw error;

        setVideoProgress(prev => ({
          ...prev,
          [contentId]: {
            progress_percentage,
            last_position: currentTime
          }
        }));
      } catch (error) {
        console.error('Error updating video progress:', error);
      }
    }, 2000);
  };

  const fetchModuleContent = async () => {
    try {
      const { data: moduleData } = await supabase
        .from('onboarding_modules')
        .select('title')
        .eq('id', moduleId)
        .single();

      if (moduleData) {
        setModuleTitle(moduleData.title);
      }

      const { data, error } = await supabase
        .from('module_content')
        .select('*')
        .eq('module_id', moduleId)
        .order('order_number', { ascending: true });

      if (error) {
        console.error('Error fetching content:', error);
        return;
      }

      if (data) {
        console.log('Fetched content:', data);
        setContent(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: ModuleContent) => {
    console.log('Editing item:', item);
    setEditingId(item.id);
    setEditUrl(item.url);
  };

  const handleSave = async (id: string) => {
    try {
      console.log('Saving new URL:', editUrl, 'for id:', id);
      
      if (!editUrl) {
        toast({
          title: "Error",
          description: "Please enter a video URL",
          variant: "destructive",
        });
        return;
      }

      const isScreenpal = editUrl.includes('screenpal.com') || editUrl.includes('go.screenpal.com');
      const isYoutube = editUrl.includes('youtube.com') || editUrl.includes('youtu.be');

      if (!isScreenpal && !isYoutube) {
        toast({
          title: "Error",
          description: "Please enter a valid Screenpal or YouTube URL",
          variant: "destructive",
        });
        return;
      }

      let finalUrl = editUrl;
      if (isScreenpal && editUrl.includes('data-id')) {
        const videoId = editUrl.split('data-id="')[1]?.split('"')[0];
        if (videoId) {
          finalUrl = `https://go.screenpal.com/player/${videoId}`;
        }
      }

      console.log('Updating database with URL:', finalUrl);

      const { data, error } = await supabase
        .from('module_content')
        .update({ url: finalUrl })
        .eq('id', id)
        .select();

      if (error) {
        console.error('Database update error:', error);
        throw error;
      }

      const { data: updatedContent, error: fetchError } = await supabase
        .from('module_content')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching updated content:', fetchError);
        throw fetchError;
      }

      if (updatedContent) {
        setContent(prevContent => 
          prevContent.map(item => 
            item.id === id ? { ...item, url: finalUrl } : item
          )
        );
      }
      
      setEditingId(null);
      setEditUrl('');
      
      toast({
        title: "Success",
        description: "Video URL has been updated",
      });
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: "Error",
        description: "Failed to update video URL",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditUrl('');
  };

  const embedYouTubeUrl = (url: string) => {
    let videoId;
    if (url.includes('youtu.be')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else {
      videoId = url.split('v=')[1]?.split('&')[0];
    }
    return `https://www.youtube.com/embed/${videoId}`;
  };

  const getVideoEmbed = (url: string, contentId: string) => {
    if (url.includes('screenpal.com') || url.includes('go.screenpal.com')) {
      const videoId = url.includes('player/') 
        ? url.split('player/')[1]?.split('?')[0] 
        : url.split('data-id="')[1]?.split('"')[0];
      return (
        <div className="space-y-2">
          <iframe 
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            scrolling="no" 
            src={`https://go.screenpal.com/player/${videoId}?width=100%&height=100%&ff=1&title=0`}
            allowFullScreen
            className="w-full aspect-video rounded-lg"
          />
          <Progress value={videoProgress[contentId]?.progress_percentage || 0} className="h-2" />
        </div>
      );
    } else {
      return (
        <div className="space-y-2">
          <iframe
            src={`${embedYouTubeUrl(url)}?enablejsapi=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full aspect-video rounded-lg"
            onLoad={(e) => {
              if (!youtubeApiReady) return;
              
              const iframe = e.target as HTMLIFrameElement;
              new window.YT.Player(iframe, {
                events: {
                  onStateChange: (event) => {
                    if (event.data === window.YT.PlayerState.PLAYING) {
                      const player = event.target;
                      const updateInterval = setInterval(() => {
                        if (player.getPlayerState() !== window.YT.PlayerState.PLAYING) {
                          clearInterval(updateInterval);
                          return;
                        }
                        updateVideoProgress(contentId, player.getCurrentTime(), player.getDuration());
                      }, 5000);
                    }
                  }
                }
              });
            }}
          />
          <Progress value={videoProgress[contentId]?.progress_percentage || 0} className="h-2" />
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link to="/onboarding">
            <Button variant="ghost" className="flex items-center gap-2 mb-4">
              <ArrowLeft className="h-4 w-4" />
              Back to Modules
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">{moduleTitle}</h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {content.map((item) => (
              <Card key={item.id} className="h-fit">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle>{item.title}</CardTitle>
                  {item.content_type === 'video' && editingId !== item.id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(item)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {item.description && (
                    <p className="text-gray-600 mb-4">{item.description}</p>
                  )}
                  {item.content_type === 'video' && (
                    <>
                      {editingId === item.id ? (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Input
                              placeholder="Paste YouTube URL or Screenpal embed code"
                              value={editUrl}
                              onChange={(e) => setEditUrl(e.target.value)}
                            />
                            <p className="text-sm text-gray-500">
                              Accepts YouTube URLs or Screenpal embed codes
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleSave(item.id)}>
                              Save
                            </Button>
                            <Button variant="outline" onClick={handleCancel}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-w-16 aspect-h-9">
                          {getVideoEmbed(item.url, item.id)}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleContent;
